import { useMemo, useState, type CSSProperties } from "react";
import type { SendAlertScreenResolved } from "@buxus/shared";
import {
  AnalyticsScreenConstants,
  buildBuxusSendAlertDto,
  listCountiesForState,
  listStatesForCountry,
  SEND_ALERT_FORM_DEFAULTS,
  type BuxusPreviewAlertOutput,
  type SendAlertFormValues,
  validateSendAlertForm,
} from "@buxus/shared";
import { BUXUS_WEBHOOK_TOKEN } from "../../platform/abp/config";
import "../../registration/RegistrationFlow.css";
import { RegReqLabel } from "../../ui/RegReqLabel";
import { AppButton } from "../../ui/AppButton";
import { postPreviewAlert, postSendAlert } from "./buxusAlertClient";
import { reportReliabilityFailure } from "../../platform/errors/webFailureReporting";
import { friendlySendAlertError } from "./sendAlertErrors";
import { EventTypes, trackEvent } from "../../platform/analytics/analytics";
import {
  classifyThrownFailure,
  outcomeFailureProps,
  outcomeSuccessProps,
} from "@buxus/shared";

const SEND_ALERT_SCREEN = AnalyticsScreenConstants.SEND_ALERT_SCREEN;
const SEND_ALERT_COMPONENT = "SendAlertScreen";

function alertLocationProps(form: SendAlertFormValues): {
  country: string;
  stateCounties: string[];
  deliveryType: string;
  stakeholderType: string;
  message: string;
} {
  return {
    country: form.country,
    stateCounties: form.state && form.county ? [`${form.state} - ${form.county}`] : [],
    deliveryType: form.deliveryType,
    stakeholderType: form.stakeholderType,
    message: form.message,
  };
}

type Props = {
  model: SendAlertScreenResolved;
  senderEmail?: string | null;
  primaryHex: string;
};

function fieldById(model: SendAlertScreenResolved, id: string) {
  return model.fields.find((f) => f.id === id);
}

export function SendAlertScreen({ model, senderEmail, primaryHex }: Props) {
  const [form, setForm] = useState<SendAlertFormValues>({ ...SEND_ALERT_FORM_DEFAULTS });
  const [alertBlurred, setAlertBlurred] = useState<Partial<Record<keyof SendAlertFormValues, true>>>({});
  const [previewAttempted, setPreviewAttempted] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [preview, setPreview] = useState<BuxusPreviewAlertOutput | null>(null);
  const [resultText, setResultText] = useState("");
  const [apiError, setApiError] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);

  const stateOptions = useMemo(() => listStatesForCountry(form.country), [form.country]);
  const countyOptions = useMemo(
    () => listCountiesForState(form.country, form.state),
    [form.country, form.state],
  );

  const fieldErrors = useMemo(() => validateSendAlertForm(form), [form]);
  const formValid = useMemo(() => Object.keys(fieldErrors).length === 0, [fieldErrors]);
  const previewChannelTotal =
    (preview?.emailCount ?? 0) + (preview?.smsCount ?? 0) + (preview?.voiceCount ?? 0);
  const sendBlockedNoRecipients = showSummary && !sendDisabled && previewChannelTotal === 0;

  const setField = (key: keyof SendAlertFormValues, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "country" && value !== prev.country) {
        next.state = "";
        next.county = "";
      }
      if (key === "state" && value !== prev.state) {
        next.county = "";
      }
      return next;
    });
  };

  function showAlertFieldError(field: keyof SendAlertFormValues): string | undefined {
    const msg = fieldErrors[field];
    if (!msg) return undefined;
    if (previewAttempted) return msg;
    if (alertBlurred[field]) return msg;
    return undefined;
  }

  const runPreview = async () => {
    setPreviewAttempted(true);
    const v = validateSendAlertForm(form);
    if (Object.keys(v).length > 0) {
      trackEvent(EventTypes.FORM_VALIDATION_FAILED, {
        component: SEND_ALERT_COMPONENT,
        screen: SEND_ALERT_SCREEN,
        action: "preview",
        fields: Object.keys(v),
        errorCount: Object.keys(v).length,
      });
      return;
    }
    setPreviewBusy(true);
    setResultText("");
    setApiError("");
    const locationProps = alertLocationProps(form);
    try {
      const dto = buildBuxusSendAlertDto(form, BUXUS_WEBHOOK_TOKEN, senderEmail);
      const out = await postPreviewAlert(dto);
      trackEvent(EventTypes.ALERT_PREVIEWED, {
        component: SEND_ALERT_COMPONENT,
        status: "success",
        ...outcomeSuccessProps(),
        contactCount: out.contactCount ?? 0,
        smsCount: out.smsCount ?? 0,
        emailCount: out.emailCount ?? 0,
        voiceCount: out.voiceCount ?? 0,
        stateCounties: out.stateCounties ?? [],
        country: locationProps.country,
        deliveryType: locationProps.deliveryType,
        stakeholderType: locationProps.stakeholderType,
        message: locationProps.message,
      });
      setPreview(out);
      setShowSummary(true);
    } catch (e) {
      const classified = classifyThrownFailure(e);
      reportReliabilityFailure(e, {
        context: "sendAlert",
        metadata: { sendAlert: { phase: "preview" } },
        failureKind: classified.failureKind,
        analyticsEvent: EventTypes.ALERT_PREVIEWED,
        analyticsProps: {
          component: SEND_ALERT_COMPONENT,
          phase: "preview",
          status: "failed",
          ...locationProps,
          ...outcomeFailureProps({
            errorMessage: classified.errorMessage,
            failureKind: classified.failureKind,
          }),
        },
      });
      setPreview(null);
      setShowSummary(false);
      setApiError(friendlySendAlertError(e, "preview"));
    } finally {
      setPreviewBusy(false);
    }
  };

  const runSend = async () => {
    const v = validateSendAlertForm(form);
    if (Object.keys(v).length > 0) {
      trackEvent(EventTypes.FORM_VALIDATION_FAILED, {
        component: SEND_ALERT_COMPONENT,
        screen: SEND_ALERT_SCREEN,
        action: "send",
        fields: Object.keys(v),
        errorCount: Object.keys(v).length,
      });
      return;
    }
    setSendBusy(true);
    setApiError("");
    const locationProps = alertLocationProps(form);
    try {
      const dto = buildBuxusSendAlertDto(form, BUXUS_WEBHOOK_TOKEN, senderEmail);
      const out = await postSendAlert(dto);
      const alertSentBase = {
        component: SEND_ALERT_COMPONENT,
        contactCount: preview?.contactCount ?? 0,
        smsCount: preview?.smsCount ?? 0,
        emailCount: preview?.emailCount ?? 0,
        voiceCount: preview?.voiceCount ?? 0,
        stateCounties:
          preview?.stateCounties && preview.stateCounties.length > 0
            ? preview.stateCounties
            : locationProps.stateCounties,
        country: locationProps.country,
        deliveryType: locationProps.deliveryType,
        stakeholderType: locationProps.stakeholderType,
        message: locationProps.message,
      };
      if (out.success === true) {
        trackEvent(EventTypes.ALERT_SENT, {
          ...alertSentBase,
          status: "success",
          ...outcomeSuccessProps(),
          jobId: out.jobId,
        });
        setResultText(model.strings.successMessage);
        setSendDisabled(true);
      } else {
        const rejectError =
          out.success === false
            ? "send-alert returned success=false"
            : "send-alert response missing success=true";
        reportReliabilityFailure(new Error(rejectError), {
          context: "sendAlert",
          metadata: { sendAlert: { phase: "send" } },
          failureKind: "http",
          analyticsEvent: EventTypes.ALERT_SENT,
          analyticsProps: {
            ...alertSentBase,
            phase: "send",
            status: "failed",
            ...outcomeFailureProps({
              errorMessage: rejectError,
              failureKind: "http",
            }),
          },
        });
        setApiError("The alert could not be queued. Please try again or contact support.");
      }
    } catch (e) {
      const classified = classifyThrownFailure(e);
      reportReliabilityFailure(e, {
        context: "sendAlert",
        metadata: { sendAlert: { phase: "send" } },
        failureKind: classified.failureKind,
        analyticsEvent: EventTypes.ALERT_SENT,
        analyticsProps: {
          component: SEND_ALERT_COMPONENT,
          phase: "send",
          status: "failed",
          contactCount: preview?.contactCount ?? 0,
          smsCount: preview?.smsCount ?? 0,
          emailCount: preview?.emailCount ?? 0,
          voiceCount: preview?.voiceCount ?? 0,
          stateCounties:
            preview?.stateCounties && preview.stateCounties.length > 0
              ? preview.stateCounties
              : locationProps.stateCounties,
          country: locationProps.country,
          deliveryType: locationProps.deliveryType,
          stakeholderType: locationProps.stakeholderType,
          message: locationProps.message,
          ...outcomeFailureProps({
            errorMessage: classified.errorMessage,
            failureKind: classified.failureKind,
          }),
        },
      });
      setApiError(friendlySendAlertError(e, "send"));
    } finally {
      setSendBusy(false);
    }
  };

  const renderSelect = (id: keyof SendAlertFormValues, options: { label: string; value: string }[]) => {
    const spec = fieldById(model, id);
    if (!spec || spec.kind !== "select") return null;
    const err = showAlertFieldError(id);
    const placeholder = spec.placeholder?.trim() || "Select";
    return (
      <label key={id} className="reg-field">
        <RegReqLabel required>{spec.label}</RegReqLabel>
        <select
          className={err ? "reg-control--error" : undefined}
          value={form[id]}
          onChange={(e) => setField(id, e.target.value)}
          onBlur={() => setAlertBlurred((p) => ({ ...p, [id]: true }))}
          aria-invalid={!!err}
          aria-label={spec.label}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {err ? <small className="reg-field-error">{err}</small> : null}
      </label>
    );
  };

  return (
    <section className="app-download-card app-send-alert">
      <h2 className="app-download-title">{model.title}</h2>
      <p className="app-send-alert-hint">
        Choose recipients and delivery, then continue to preview before sending.
      </p>

      {!showSummary ? (
        <>
          {model.sections.map((section) => (
            <div key={section.heading} className="app-send-alert-section">
              <h3 className="app-send-alert-section-title">{section.heading}</h3>
              <div className="reg-grid">
                {section.fieldIds.map((fid) => {
                  if (fid === "stakeholderType") {
                    return renderSelect("stakeholderType", model.stakeholderOptions);
                  }
                  if (fid === "country") {
                    return renderSelect("country", model.countryOptions);
                  }
                  if (fid === "state") {
                    return renderSelect("state", stateOptions);
                  }
                  if (fid === "county") {
                    return renderSelect("county", countyOptions);
                  }
                  if (fid === "message") {
                    const spec = fieldById(model, "message");
                    if (!spec || spec.kind !== "textarea") return null;
                    const err = showAlertFieldError("message");
                    return (
                      <label key="message" className="reg-field">
                        <RegReqLabel required>{spec.label}</RegReqLabel>
                        <textarea
                          className={err ? "reg-control--error" : undefined}
                          value={form.message}
                          onChange={(e) => setField("message", e.target.value)}
                          onBlur={() => setAlertBlurred((p) => ({ ...p, message: true }))}
                          rows={5}
                          aria-invalid={!!err}
                          aria-label={spec.label}
                        />
                        {err ? <small className="reg-field-error">{err}</small> : null}
                      </label>
                    );
                  }
                  if (fid === "deliveryType") {
                    return renderSelect("deliveryType", model.deliveryOptions);
                  }
                  return null;
                })}
              </div>
            </div>
          ))}
          {apiError ? (
            <p className="app-send-alert-api-error" role="alert">
              {apiError}
            </p>
          ) : null}
          <div className="app-send-alert-actions">
            <AppButton
              type="button"
              variant="primary"
              loading={previewBusy}
              loadingLabel="Loading Preview…"
              disabled={!formValid}
              onClick={() => void runPreview()}
              style={{ backgroundColor: primaryHex, borderColor: primaryHex } satisfies CSSProperties}
            >
              {model.strings.continueCta}
            </AppButton>
          </div>
        </>
      ) : (
        <>
          <div className="app-send-alert-preview">
            <h3 className="app-send-alert-section-title">{model.strings.previewHeading}</h3>
            <div className="app-send-alert-stats">
              <p className="app-send-alert-stat">Contacts: {preview?.contactCount ?? "—"}</p>
              <p className="app-send-alert-stat">SMS: {preview?.smsCount ?? "—"}</p>
              <p className="app-send-alert-stat">Email: {preview?.emailCount ?? "—"}</p>
              <p className="app-send-alert-stat">Voice: {preview?.voiceCount ?? "—"}</p>
            </div>
          </div>
          {sendBlockedNoRecipients ? (
            <p className="app-send-alert-hint" role="status">
              No recipients matched this preview. Tap Edit to try again.
            </p>
          ) : null}
          {apiError ? (
            <p className="app-send-alert-api-error" role="alert">
              {apiError}
            </p>
          ) : null}
          <div className="app-send-alert-actions app-send-alert-actions-row">
            <AppButton
              type="button"
              variant="outline"
              disabled={sendBusy}
              onClick={() => {
                setShowSummary(false);
                setPreview(null);
                setPreviewAttempted(false);
                setAlertBlurred({});
                setApiError("");
                setResultText("");
                setSendDisabled(false);
              }}
            >
              Edit
            </AppButton>
            <AppButton
              type="button"
              variant="primary"
              loading={sendBusy}
              loadingLabel="Sending…"
              disabled={sendDisabled || previewChannelTotal === 0}
              onClick={() => void runSend()}
              style={{ backgroundColor: primaryHex, borderColor: primaryHex } satisfies CSSProperties}
            >
              {model.strings.sendCta}
            </AppButton>
          </div>
          {resultText ? (
            <p className="app-send-alert-result" role="status">
              {resultText}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
